import React, { useEffect, useState } from 'react';
import { TextField } from '@mui/material';
import { Card, Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { BsBarChartLineFill } from 'react-icons/bs';
import { FaCog } from 'react-icons/fa';
import { HiOutlineInformationCircle } from 'react-icons/hi';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { InternalRoutes } from '../../../../../enum/InternalRoutes';
import { PackagesEnum } from '../../../../../enum/PackageEnum';
import { ICampaignOverview } from '../../../../../model/briefing/overview/ICampaignOverview';
import { ICreatorTypes } from '../../../../../model/briefing/step1/ICreatorTypes';
import { IOfferItems } from '../../../../../model/offer/IOfferItems';
import { IParams } from '../../../../../model/shared/IParams';
import { ConfirmationModal } from '../../../../../shared/components/modal/ConfirmationModal';
import { InfoBox } from '../../../../../shared/components/alert/InfoBox';
import { CustomImageHandler } from '../../../../../shared/components/CustomImageHandler';
import ToggleSwitch from '../../../../../shared/components/ToggleSwitch';
import {
    calculateRecommendedBudgetRange,
    checkContractType,
    downloadFile,
    formatDate,
    hasAccessRights,
    openCalendlyPopup,
} from '../../../../../shared/functions/Functions';
import { IAllStates } from '../../../../../store/rootReducer';
import { gtmService } from '../../../../../utils/gtm.service';
import { priceSplitter } from '../../../../../utils/helpers/priceSplitter';
import { ErrorToast, SuccessToast, WarningToast } from '../../../../../utils/toasters';
import TooltipPackage from '../../../../shared/packages/components/TooltipPackage';
import BillingInfoModal from '../../../../shared/user/components/BillingInfoModal';
import { CampaignCreateModal } from '../modal/CampaignCreateModal';
import { BriefingService } from '../service';
import { CampaignOverviewCard } from './shared/CampaignOverviewCard';
import { CampaignTypeCard } from './shared/CampaignTypeCard';
import { CategoryMultiSelect } from './shared/CategoryMultiSelect';
import { ProgressBarCampaign } from './shared/ProgressBarCampaign';
import { StepperRouting } from './shared/StepperRouting';
import { brandedContent, organicReach, usageRights } from './StepSeven';
import { selectedCampaignSlice } from '../../../../../store/brand/activeCampaignSlice';
import { CampaignManagementOverviewCard } from './shared/CampaignManagementOverviewCard';
import { FindCreatorsInfo } from './shared/FindCreatorsInfo';
import DOMPurify from 'dompurify';
import { AccessRightEnum } from '../../../../../enum/AccessRightEnum';
import FilePlaceholder from '../../../../../shared/components/placeholder/FilePlaceholder';
import { ContractTypeCard } from './shared/ContractTypeCard';
import { CampaignSharingType } from '../../../../../enum/CampaignSharingTypeEnum';
import { IEditCampaignModalProps } from '../dto/ICreateCampaign';
import { Fancybox } from '../../../../../shared/components/Fancybox';
import CardLayout from '../../../../../shared/components/card/CardLayout';
import { CampaignValidationStatusEnum } from '../../../../../enum/CampaignValidationStatusEnum';
import TooltipOverlay from '../../../../../shared/components/tooltip/Tooltip';
import PayoutSectionOverview from './shared/PayoutSectionOverview';
import SelectGroupSection from './SelectGroupSection';
import { FiInfo } from 'react-icons/fi';
import ItemValueSection from '../../../../../shared/components/offer/ItemValueSection';
import { BriefingOverviewLeftSkeleton } from './shared/BriefingOverviewSkeletons';

export const BriefingOverview = () => {
    const [currentPublishStatus, setCurrentPublishStatus] = useState('published');
    const [showBriefingModal, setShowBriefingModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [displayBudgetError, setDisplayBudgetError] = useState(false);
    const [currentInformations, setCurrentInformations] = useState<ICampaignOverview>();
    const [isBillingInfoWarningOpened, setIsBillingInfoWarningOpened] = useState(false);
    const [isBillingModalOpened, setIsBillingModalOpened] = useState(false);
    const [editBriefingDetails, setEditBriefingDetails] = useState<IEditCampaignModalProps>({
        isEdit: false,
        isContractType: false,
    });
    const { completionInfo, userType, company, isGeneralClient } = useSelector((state: IAllStates) => state.auth.user);
    const language = useSelector((state: IAllStates) => state.auth.user.locale);
    const { id } = useParams<keyof IParams>();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isCharity = currentInformations?.isCharity;
    // const contractType = checkContractType(!!currentInformations?.isLongTerm, !!currentInformations?.parent?.id)
    const isLongTermCampaign = currentInformations?.isLongTerm;
    const isApproved = currentInformations?.approvalStatus === CampaignValidationStatusEnum.APPROVED;
    const isChildCampaign = !!currentInformations?.parent?.id;
    const isEditTimelineButtonLocked = isApproved && isLongTermCampaign;
    const showEditIcon = !isGeneralClient;
    const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        if (!!id) {
            BriefingService.getBriefingByStep(id, 'summary')
                .then(response => {
                    const filtered = response?.campaignCategories?.map((category: any) => {
                        return {
                            ...category,
                            path: `/icons/categories/${category?.campaignCategory?.name}.svg`,
                        };
                    });
                    sessionStorage.setItem('activeCampaignId', response?.id);
                    dispatch(selectedCampaignSlice.actions.setActiveCampaignId(String(response?.id)));
                    setCurrentInformations({
                        ...response,
                        campaignCategories: filtered,
                    });
                    if (response?.isLongTerm || response?.status === 'private') {
                        setCurrentPublishStatus('private');
                    }
                })
                .catch(error => ErrorToast(error))
                .finally(() => setIsLoading(false));
        }
    }, [id, showBriefingModal]);

    // CODE / CALCULATION FOR PREFILL BUDGET RANGE

    // useEffect(() => {
    //     const budgetRanges = currentInformations?.creatorType?.budgetRanges;
    //     const grossBudget = currentInformations?.grossBudget;
    //     if (!!budgetRanges?.length) {
    //         const specifyBudget = (Number(budgetRanges[0]?.recommendedMinBudget ?? 0)
    //             +
    //             Number(budgetRanges[0]?.recommendedMaxBudget ?? 0)) / 2;
    //         setCurrentInformations((prev: any) => ({
    //             ...prev,
    //             grossBudget: !!grossBudget ? grossBudget : specifyBudget >= 1000 ? specifyBudget : 1000,
    //         }));
    //     }
    // }, [currentInformations?.creatorType?.budgetRanges]);

    const openStep = (step: number) => {
        if (currentInformations?.isDeleted) {
            return WarningToast(t('brand.campaign.book.offer.overview_card.campaign_was_deleted'));
        }
        navigate(`${InternalRoutes.BRAND_CAMPAIGN_CREATE}/${id}/step${step}`);
    };

    const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        if (name === 'grossBudget' && +value >= 1000) {
            setDisplayBudgetError(false);
        }
        setCurrentInformations((prev: any) => ({ ...prev, [name]: value }));
    };

    const closeModal = () => setShowBriefingModal(false);

    const openModal = (editContractType?: boolean) => {
        setEditBriefingDetails({
            isEdit: true,
            isContractType: editContractType,
        });
        setShowBriefingModal(true);
    };

    const groupByPlatforms = currentInformations?.platforms?.reduce((group: any, product) => {
        const { socialPlatform } = product;
        const { platform } = socialPlatform;
        group[platform] = group[platform] ?? [];
        group[platform].push(product);
        return group;
    }, {});

    const publishCampaignHandler = () => {
        if (
            Number((currentInformations?.grossBudget ?? 0) >= 1000) ||
            isCharity ||
            isChildCampaign ||
            !hasAccessRights(AccessRightEnum.CAN_EXPORT_MEASURE)
        ) {
            if (id) {
                BriefingService.updateBriefingByStep(
                    id,
                    {
                        grossBudget: Number(currentInformations?.grossBudget),
                        status: currentPublishStatus,
                        isCharity: isCharity,
                        charityValueDesc: isCharity ? currentInformations?.charityValueDesc : undefined,
                    },
                    'publish'
                )
                    .then(response => {
                        if (response?.id) {
                            gtmService('campaign_created');
                            SuccessToast(`${t('general.alert.success_published_campaign')}`);
                            navigate(`/brand/dashboard`);
                        }
                    })
                    .catch(error => ErrorToast(error));
            }
        } else {
            const err = {
                response: {
                    data: {
                        message: 'Total budget invalid',
                    },
                },
            };
            ErrorToast(err);
        }
    };

    const duplicateCampaign = () => {
        BriefingService.updateBriefingByStep(id!, {}, 'duplicate')
            .then(response => {
                if (response?.id) {
                    SuccessToast(`${t('brand.campaign.dashboard.modal.toasts.success_duplicated_campaign')}`);
                }
            })
            .catch(error => ErrorToast(error));
    };

    const calculateSavedAmount = (plannedBudget: number, companyFee: number) => {
        if (company?.package?.name === PackagesEnum.BASIC) {
            return plannedBudget * 0.15;
        }
        return plannedBudget * (companyFee - 0.75);
    };

    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://assets.calendly.com/assets/external/widget.js';
        script.async = true;
        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, []);

    return (
        <>
            <BillingInfoModal
                show={isBillingModalOpened}
                closeModal={() => setIsBillingModalOpened(false)}
                userType={userType}
            />
            <CampaignCreateModal
                show={showBriefingModal}
                closeModal={closeModal}
                editDetail={editBriefingDetails}
                campaignId={+id!}
            />
            <ConfirmationModal
                description={t('general.updateBillingInfoOverview')}
                showSuccessButton={!completionInfo?.billingInfo}
                show={isBillingInfoWarningOpened}
                iconUrl={`/icons/profile/online-shopping.png`}
                action={() => {
                    setIsBillingInfoWarningOpened(false);
                    setIsBillingModalOpened(true);
                }}
                closeModal={() => setIsBillingInfoWarningOpened(false)}
            />
            <ConfirmationModal
                title={t('brand.campaign.briefing.overview_card.confirm_title')}
                saveBtn="send"
                cancelBtn="cancel"
                classText=""
                description={t('brand.campaign.briefing.overview_card.confirm_message', {
                    campaignName: currentInformations?.title,
                })}
                htmlDescription={
                    !isCharity &&
                    hasAccessRights(AccessRightEnum.CAN_EXPORT_MEASURE) && (
                        <TooltipOverlay
                            placement="right"
                            className="w-max-content mx-auto"
                            content={t('brand.campaign.briefing.overview_card.confirm_description', {
                                totalBudget: priceSplitter(
                                    calculateRecommendedBudgetRange(
                                        currentInformations?.creatorType?.budgetRanges ?? [],
                                        currentInformations?.brandedContent?.budget ?? 0
                                    ) ?? 0
                                ),
                            })}
                        >
                            <HiOutlineInformationCircle size={20} />
                        </TooltipOverlay>
                    )
                }
                action={() => publishCampaignHandler()}
                show={showConfirmModal}
                closeModal={() => setShowConfirmModal(false)}
            />
            <Row className="mb-5 pb-5 flex-column-reverse flex-md-row">
                <Col xl={8}>
                    {currentInformations?.isDeleted ? (
                        <div>
                            <InfoBox
                                variant="danger"
                                title="influence.vision info"
                                text={t('brand.campaign.book.offer.overview_card.campaign_deleted_duplicate')}
                                buttonName={t('general.button.duplicate')}
                                action={duplicateCampaign}
                            />
                        </div>
                    ) : (
                        <div className="mb-4">
                            <ProgressBarCampaign
                                title={t('brand.campaign.book.offer.overview_card.overview')}
                                percent={100}
                                showBackButton
                            />
                        </div>
                    )}
                    {isLoading ? (
                        <BriefingOverviewLeftSkeleton withBudgetCard={!isGeneralClient} />
                    ) : (
                        <>
                            <div className="mb-3">
                                <ContractTypeCard
                                    contractType={checkContractType(
                                        currentInformations?.isLongTerm ?? true,
                                        !!currentInformations?.parent?.id
                                    )}
                                    isCampaignDeleted={currentInformations?.isDeleted}
                                    openEditCampaignModal={openModal}
                                    longTermId={currentInformations?.parent?.id}
                                />
                            </div>
                            <div
                                className={`mb-3 ${
                                    currentInformations?.sharingType === CampaignSharingType.OWN_MANAGEMENT && 'd-none'
                                }`}
                            >
                                <CampaignManagementOverviewCard
                                    type={currentInformations?.sharingType}
                                    company={currentInformations?.sharingCompany}
                                />
                            </div>
                            <CardLayout classCard="mb-3">
                                <div className="d-flex justify-content-between mb-2">
                                    <h5>{t('brand.campaign.briefing.strategy')}</h5>
                                    {showEditIcon && (
                                        <TooltipOverlay content={t('brand.campaign.briefing.edit_section')}>
                                            <FaCog id="edit-icon" onClick={() => openStep(1)} />
                                        </TooltipOverlay>
                                    )}
                                </div>
                                <Row className="mb-4">
                                    <h6 className="text-muted">{t('brand.campaign.briefing.campaign_goal')}</h6>

                                    <Col sm={4}>
                                        <CustomImageHandler
                                            thumbnailPath={currentInformations?.campaignGoal?.goalImage?.thumbnail}
                                            photoPath={
                                                currentInformations?.campaignGoal?.goalImage?.thumbnail as string
                                            }
                                            classes="img-cover default-radius w-100 max-h-180px"
                                            altTag={currentInformations?.title as string}
                                        />
                                    </Col>
                                    <Col sm={8} className="mt-3 mt-sm-0">
                                        <h6 className="text-primary">
                                            <BsBarChartLineFill className="text-dark" size={20} />{' '}
                                            {currentInformations?.campaignGoal?.title ?? '-'}
                                        </h6>
                                        <p>
                                            {language === 'en'
                                                ? currentInformations?.campaignGoal?.story
                                                : currentInformations?.campaignGoal?.storyGerman}
                                        </p>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={4}>
                                        <h6 className="text-muted">
                                            {t('brand.campaign.briefing.content_creator_type')}
                                        </h6>

                                        <CampaignTypeCard item={currentInformations?.creatorType as ICreatorTypes} />
                                    </Col>
                                    <Col md={8}>
                                        <h6 className="text-muted mb-0 mt-3 mt-md-0">
                                            {t('brand.campaign.briefing.social_media_platform')}
                                        </h6>
                                        <div className="d-flex flex-wrap mb-2">
                                            {!!currentInformations?.mainPlatforms?.length
                                                ? currentInformations?.mainPlatforms?.map(socialPlatform => (
                                                      <CategoryMultiSelect
                                                          item={socialPlatform}
                                                          isFilterDisabled={true}
                                                          key={socialPlatform?.id}
                                                      />
                                                  ))
                                                : '-'}
                                        </div>
                                        <div className="d-flex align-content-center">
                                            <div className="mt-4">
                                                <h6 className="text-muted">
                                                    {t('brand.campaign.briefing.step1.payment_type')}
                                                </h6>
                                                <div className="d-flex mr-2">
                                                    {isCharity ? (
                                                        <p className="mb-0 mr-3">
                                                            {t('brand.account.price_list_modal.barter_deals')}
                                                        </p>
                                                    ) : (
                                                        <p className="mb-0 mr-3">
                                                            {t('brand.account.price_list_modal.paid_campaign')}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            {!isCharity && hasAccessRights(AccessRightEnum.CAN_EXPORT_MEASURE) && (
                                                <div>
                                                    <h6 className="text-muted mt-4">
                                                        {t('brand.campaign.briefing.budget_range')}
                                                    </h6>
                                                    <h6 className="euro-symbol">
                                                        {priceSplitter(
                                                            currentInformations?.creatorType?.budgetRanges?.[0]
                                                                ?.recommendedMinBudget ?? 0
                                                        )}
                                                        -{' '}
                                                        {priceSplitter(
                                                            currentInformations?.creatorType?.budgetRanges?.[0]
                                                                ?.recommendedMaxBudget ?? 0
                                                        )}
                                                    </h6>
                                                </div>
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                            </CardLayout>

                            <CardLayout classCard="mb-3">
                                <div className="d-flex justify-content-between mb-2">
                                    <h5>{t('brand.campaign.briefing.content')}</h5>
                                    {showEditIcon && (
                                        <TooltipOverlay content={t('brand.campaign.briefing.edit_section')}>
                                            <FaCog id="edit-icon" onClick={() => openStep(2)} />
                                        </TooltipOverlay>
                                    )}
                                </div>
                                <div className="mb-2 mt-3">
                                    <h6 className="text-muted">{t('brand.campaign.briefing.product_desc')}</h6>
                                    <div
                                        className="remove-html-styling"
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(currentInformations?.details ?? '-', {
                                                ADD_ATTR: ['target'],
                                            }),
                                        }}
                                    ></div>
                                </div>
                                <div className="mb-2 mt-4">
                                    <h6 className="text-muted">{t('brand.campaign.briefing.campaign_desc')}</h6>
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(currentInformations?.adDescription ?? '-', {
                                                ADD_ATTR: ['target'],
                                            }),
                                        }}
                                    ></div>
                                </div>
                                <div className="mb-2 mt-4">
                                    <h6 className="text-muted">{t('brand.campaign.briefing.relevant_hash')}</h6>
                                    <p>{currentInformations?.hashtags ?? '-'}</p>
                                </div>
                                {!!currentInformations?.profileTags && (
                                    <div className="mb-2 mt-4">
                                        <h6 className="text-muted">{t('influencer.offer_management.modal.tagging')}</h6>
                                        <p>{currentInformations?.profileTags ?? '-'}</p>
                                    </div>
                                )}
                                <div className="mb-2 mt-4">
                                    <h6 className="text-muted">{t('brand.campaign.briefing.inspiration_links')}</h6>
                                    <a href={currentInformations?.url} target="_blank" rel="noreferrer">
                                        {currentInformations?.url ?? '-'}
                                    </a>
                                </div>
                                <div className="mb-2 mt-4">
                                    <h6 className="text-muted">{t('brand.campaign.briefing.content_moodboard')}</h6>

                                    <Fancybox>
                                        <div className="row">
                                            {!!currentInformations?.campaignImages?.length
                                                ? currentInformations?.campaignImages
                                                      ?.filter(image => image?.purpose === 'style_mood_image')
                                                      ?.map(moonBoard => {
                                                          return (
                                                              <div
                                                                  className="col-md-4 my-2 position-relative"
                                                                  key={moonBoard?.id}
                                                              >
                                                                  <FilePlaceholder
                                                                      file={moonBoard}
                                                                      styleContent={{ height: 130 }}
                                                                      nameFancybox="moonboard-overview"
                                                                      handleDownload={() =>
                                                                          downloadFile(
                                                                              moonBoard?.path,
                                                                              moonBoard?.title
                                                                          )
                                                                      }
                                                                  />
                                                              </div>
                                                          );
                                                      })
                                                : '-'}
                                        </div>
                                    </Fancybox>
                                </div>
                            </CardLayout>

                            <CardLayout classCard="mb-3">
                                <div className="d-flex justify-content-between mb-3">
                                    <h5>{t('brand.campaign.briefing.timeline')}</h5>
                                    {!isEditTimelineButtonLocked && showEditIcon && (
                                        <TooltipOverlay content={t('brand.campaign.briefing.edit_section')}>
                                            <FaCog id="edit-icon" onClick={() => openStep(3)} />
                                        </TooltipOverlay>
                                    )}
                                </div>
                                <div
                                    className="d-flex flex-wrap justify-content-between"
                                    style={{ columnGap: '44px', rowGap: '28px' }}
                                >
                                    {isLongTermCampaign ? (
                                        <>
                                            <div>
                                                <h6 className="text-muted">
                                                    {' '}
                                                    {t('brand.campaign.briefing.step3.start_date')}
                                                </h6>
                                                <p className="mb-0">
                                                    {!!currentInformations?.date?.length
                                                        ? formatDate(currentInformations?.date as string)
                                                        : '-'}{' '}
                                                </p>
                                            </div>
                                            <div>
                                                <h6 className="text-muted">
                                                    {' '}
                                                    {t('brand.campaign.briefing.step3.campaign_duration')}
                                                </h6>
                                                <p className="mb-0">
                                                    {!!currentInformations?.invoiceDuration
                                                        ? t(
                                                              `brand.campaign.briefing.step3.campaign_duration_enum.${currentInformations?.invoiceDuration}`
                                                          )
                                                        : '-'}
                                                </p>
                                            </div>
                                            <div>
                                                <h6 className="text-muted">
                                                    {' '}
                                                    {t('brand.campaign.briefing.step8.payout_method')}
                                                </h6>
                                                <p className="mb-0">
                                                    {!!currentInformations?.invoicePeriod
                                                        ? t(
                                                              `brand.campaign.briefing.step8.payment_method.${currentInformations?.invoicePeriod}`
                                                          )
                                                        : '-'}
                                                </p>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div>
                                                <h6 className="text-muted">
                                                    {' '}
                                                    {t('brand.campaign.briefing.application_deadline')}
                                                </h6>
                                                <p className="mb-0">
                                                    {!!currentInformations?.date?.length
                                                        ? formatDate(currentInformations?.date as string)
                                                        : '-'}{' '}
                                                </p>
                                            </div>
                                            <div>
                                                <h6 className="text-muted">
                                                    {' '}
                                                    {t('brand.campaign.briefing.publication_period')}
                                                </h6>
                                                <p className="mb-0">
                                                    {currentInformations?.publishFrom
                                                        ? formatDate(currentInformations?.publishFrom as string)
                                                        : '-'}{' '}
                                                    -{' '}
                                                    {currentInformations?.publishTo
                                                        ? formatDate(currentInformations?.publishTo as string)
                                                        : '-'}
                                                </p>
                                            </div>
                                            <div>
                                                <h6 className="text-muted">
                                                    {' '}
                                                    {t('brand.campaign.briefing.content_approval')}
                                                </h6>
                                                {currentInformations?.isReviewed ? (
                                                    <p className="mb-0">
                                                        {t('brand.campaign.briefing.days_before_publishing', {
                                                            numberOfDays: currentInformations?.reviewDays,
                                                        })}
                                                    </p>
                                                ) : (
                                                    'No'
                                                )}
                                            </div>
                                            <div>
                                                <h6 className="text-muted">
                                                    {' '}
                                                    {t('brand.campaign.briefing.step3.postingDeadline')}
                                                </h6>
                                                <p className="mb-0">
                                                    {!!currentInformations?.date?.length
                                                        ? formatDate(currentInformations?.postingDeadline as string)
                                                        : '-'}{' '}
                                                </p>
                                            </div>
                                            {company?.canSkipReporting && (
                                                <div>
                                                    <h6 className="text-muted">
                                                        {t('brand.campaign.briefing.step3.canSkipReporting')}
                                                    </h6>
                                                    <p className="mb-0">
                                                        {currentInformations?.canSkipReporting
                                                            ? t('general.button.yes')
                                                            : t('general.button.no')}
                                                    </p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </CardLayout>

                            <CardLayout classCard="mb-3">
                                <div className="d-flex justify-content-between mb-2">
                                    <h5>{t('brand.campaign.briefing.placements')}</h5>
                                    {showEditIcon && (
                                        <TooltipOverlay content={t('brand.campaign.briefing.edit_section')}>
                                            <FaCog id="edit-icon" onClick={() => openStep(4)} />
                                        </TooltipOverlay>
                                    )}
                                </div>
                                <div className="d-flex flex-wrap" style={{ gap: '16px' }}>
                                    {!!currentInformations?.platforms?.length
                                        ? Object.values(groupByPlatforms)?.map((groupList, index) => (
                                              <React.Fragment key={index}>
                                                  <div className={`w-100`} />
                                                  {(groupList as IOfferItems[])?.map(platform => {
                                                      return (
                                                          <div
                                                              className={`d-flex align-items-center my-1 mr-3`}
                                                              key={platform?.id}
                                                          >
                                                              <img
                                                                  src={platform?.socialPlatform?.path}
                                                                  width={30}
                                                                  alt="Social platform icon"
                                                              />
                                                              <p className="mb-0 ml-2">
                                                                  {' '}
                                                                  {t(
                                                                      `general.platforms.${platform?.socialPlatform?.name}`
                                                                  )}
                                                              </p>
                                                              <h6 className="mb-0 ml-2 text-primary">
                                                                  x{platform?.nbPosts}
                                                              </h6>
                                                          </div>
                                                      );
                                                  })}
                                              </React.Fragment>
                                          ))
                                        : '-'}
                                </div>
                            </CardLayout>

                            <CardLayout classCard="mb-3">
                                <div className="d-flex justify-content-between mb-2">
                                    <h5>{t('brand.campaign.briefing.content_creator')}</h5>
                                    {showEditIcon && (
                                        <TooltipOverlay content={t('brand.campaign.briefing.edit_section')}>
                                            <FaCog id="edit-icon" onClick={() => openStep(5)} />
                                        </TooltipOverlay>
                                    )}
                                </div>
                                <div className="d-flex flex-wrap mb-3" style={{ gap: '20px' }}>
                                    <div className="mr-5">
                                        <h6 className="text-muted"> {t('brand.campaign.book.offer.modal.location')}</h6>
                                        <p>
                                            {!!currentInformations?.countries?.length
                                                ? currentInformations?.countries
                                                      ?.map(country => `${t(`general.countries.${country?.name}`)}`)
                                                      .join(', ')
                                                : '-'}{' '}
                                        </p>
                                    </div>
                                    <div className="mr-5">
                                        <h6 className="text-muted">{t('brand.campaign.book.invite.filters.gender')}</h6>
                                        <p>{t(`general.gender.${currentInformations?.gender ?? 'null'}`)}</p>
                                    </div>
                                    <div className=" mr-5">
                                        <h6 className="text-muted">{t('brand.campaign.briefing.age_range')}</h6>
                                        <p>
                                            {currentInformations?.ageMin}-{currentInformations?.ageMax}
                                        </p>
                                    </div>
                                </div>
                                <div>
                                    <h6 className="text-muted">{t('brand.campaign.briefing.categories')}</h6>
                                    <div className="d-flex flex-wrap my-3">
                                        {!!currentInformations?.campaignCategories?.length
                                            ? currentInformations?.campaignCategories
                                                  ?.map(catIcon => ({
                                                      ...catIcon,
                                                      name: catIcon.campaignCategory.name,
                                                  }))
                                                  ?.map(category => (
                                                      <CategoryMultiSelect
                                                          item={category as any}
                                                          name={t(
                                                              `general.category.${category.campaignCategory?.name}`
                                                          )}
                                                          isFilterDisabled={true}
                                                          key={category?.id}
                                                      />
                                                  ))
                                            : '-'}
                                    </div>
                                </div>
                            </CardLayout>

                            <CardLayout classCard="mb-3">
                                <div className="d-flex justify-content-between mb-2">
                                    <h5>{t('brand.campaign.briefing.audience')}</h5>
                                    {showEditIcon && (
                                        <TooltipOverlay content={t('brand.campaign.briefing.edit_section')}>
                                            <FaCog id="edit-icon" onClick={() => openStep(6)} />
                                        </TooltipOverlay>
                                    )}
                                </div>
                                <div className="d-flex flex-wrap mb-3" style={{ gap: '18px' }}>
                                    <div className="mr-5">
                                        <h6 className="text-muted">
                                            {' '}
                                            {t('brand.campaign.briefing.audience_location')}
                                        </h6>
                                        <p>
                                            {!!currentInformations?.audienceCountriesArray?.length
                                                ? currentInformations?.audienceCountriesArray
                                                      ?.map(country => `${t(`general.countries.${country}`)}`)
                                                      .join(', ')
                                                : '-'}
                                        </p>
                                    </div>
                                    <div className="mr-5">
                                        <h6 className="text-muted"> {t('brand.campaign.briefing.audience_gender')}</h6>
                                        <p>{t(`general.gender.${currentInformations?.audienceGender ?? 'null'}`)}</p>
                                    </div>
                                    <div className=" mr-5">
                                        <h6 className="text-muted"> {t('brand.campaign.briefing.audience_age')}</h6>
                                        <p>
                                            {currentInformations?.audienceAgeMin}-{currentInformations?.audienceAgeMax}
                                        </p>
                                    </div>
                                    {/*<div className=" mr-3 mr-md-5">*/}
                                    {/*    <h6 className="text-muted"> {t('brand.campaign.briefing.audience_min')}</h6>*/}
                                    {/*    <p>15-23</p>*/}
                                    {/*</div>*/}
                                </div>
                                <div>
                                    <h6 className="text-muted"> {t('brand.campaign.briefing.audience_desc')}</h6>
                                    <p>{currentInformations?.audienceDesc ?? '-'}</p>
                                </div>
                            </CardLayout>

                            <CardLayout classCard="mb-3">
                                <div className="d-flex justify-content-between mb-2">
                                    <h5>{t('brand.campaign.briefing.distribution')}</h5>
                                    {showEditIcon && (
                                        <TooltipOverlay content={t('brand.campaign.briefing.edit_section')}>
                                            <FaCog id="edit-icon" onClick={() => openStep(7)} />
                                        </TooltipOverlay>
                                    )}
                                </div>
                                <Card className="mb-4">
                                    <div className="d-sm-flex">
                                        <Col md={4} className="p-3 p-sm-0">
                                            <img
                                                src="/tempAssets/img-organic-reach.png"
                                                className="w-100 default-radius h-100 img-cover max-h-180px"
                                                height={140}
                                                alt="Campaign-img"
                                            />
                                        </Col>
                                        <Col md={8} className="d-flex align-items-center pl-0">
                                            <Card.Body>
                                                <h5 className="font-weight-bold">
                                                    {t(organicReach?.[0]?.name ?? '-')}
                                                </h5>
                                                <p className="text-muted mb-0">
                                                    {t(organicReach?.[0]?.description ?? '-')}
                                                </p>
                                            </Card.Body>
                                        </Col>
                                    </div>
                                </Card>
                                <h6 className="text-muted">{t('brand.campaign.book.offer.modal.branded_content')}</h6>
                                {currentInformations?.brandedContent?.id ? (
                                    <>
                                        <Card className="mb-4">
                                            <div className="d-sm-flex">
                                                <Col md={4} className="p-3 p-sm-0">
                                                    <img
                                                        src={
                                                            brandedContent?.find(
                                                                content =>
                                                                    content.key ===
                                                                    currentInformations?.brandedContent?.goal
                                                            )?.img
                                                        }
                                                        className="w-100 default-radius img-cover max-h-180px h-100"
                                                        height={140}
                                                        alt="Campaign-img"
                                                    />
                                                </Col>
                                                <Col md={8} className="d-flex align-items-center pl-0">
                                                    <Card.Body>
                                                        <h5 className="font-weight-bold">
                                                            {currentInformations?.brandedContent?.goal
                                                                ? t(
                                                                      `brand.campaign.briefing.brandedContent.${currentInformations?.brandedContent?.goal}`
                                                                  )
                                                                : '-'}
                                                        </h5>
                                                        <p className="text-muted mb-0">
                                                            {t(
                                                                brandedContent?.find(
                                                                    content =>
                                                                        content.key ===
                                                                        currentInformations?.brandedContent?.goal
                                                                )?.description ?? '-'
                                                            )}
                                                        </p>
                                                    </Card.Body>
                                                </Col>
                                            </div>
                                        </Card>
                                        <h6>{t('brand.campaign.briefing.how_use_content')}</h6>
                                        <p>{currentInformations?.brandedContent?.description ?? '-'}</p>
                                        <Row>
                                            <Col>
                                                <h6>{t('brand.campaign.briefing.brandedContent_duration')}</h6>
                                                <p>
                                                    {currentInformations?.brandedContent?.duration
                                                        ? t(
                                                              `brand.campaign.briefing.step8.${currentInformations?.brandedContent?.duration}`
                                                          )
                                                        : '-'}
                                                </p>
                                            </Col>
                                            <Col>
                                                <h6>{t('brand.campaign.briefing.brandedContent_location')}</h6>
                                                <p>
                                                    {!!currentInformations?.brandedContent?.countries?.length
                                                        ? currentInformations?.brandedContent?.countries?.map(
                                                              (country: { name: string }) => (
                                                                  <React.Fragment key={country?.name}>
                                                                      {t(`general.countries.${country?.name}`)},{' '}
                                                                  </React.Fragment>
                                                              )
                                                          )
                                                        : '-'}
                                                </p>
                                            </Col>
                                        </Row>
                                    </>
                                ) : (
                                    '-'
                                )}
                                <h6 className="text-muted mt-3">{t('brand.campaign.book.offer.modal.usage_rights')}</h6>
                                {currentInformations?.usageRights?.id ? (
                                    <>
                                        <Card className="mb-4">
                                            <div className="d-sm-flex">
                                                <Col md={4} className="p-3 p-sm-0">
                                                    <img
                                                        src={
                                                            usageRights?.find(
                                                                right =>
                                                                    right.key ===
                                                                    currentInformations?.usageRights?.mediaType
                                                            )?.img
                                                        }
                                                        className="w-100 default-radius h-100 max-h-180px img-cover"
                                                        height={140}
                                                        alt="Campaign-img"
                                                    />
                                                </Col>
                                                <Col md={8} className="d-flex align-items-center px-0">
                                                    <Card.Body>
                                                        <h5 className="font-weight-bold">
                                                            {t(
                                                                usageRights?.find(
                                                                    right =>
                                                                        right.key ===
                                                                        currentInformations?.usageRights?.mediaType
                                                                )?.name ?? '-'
                                                            )}
                                                        </h5>
                                                        <p className="text-muted mb-0">
                                                            {t(
                                                                usageRights?.find(
                                                                    right =>
                                                                        right.key ===
                                                                        currentInformations?.usageRights?.mediaType
                                                                )?.description ?? '-'
                                                            )}
                                                        </p>
                                                    </Card.Body>
                                                </Col>
                                            </div>
                                        </Card>
                                        <h6>{t('brand.campaign.briefing.how_use_content')}</h6>
                                        <p>{currentInformations?.usageRights?.description ?? '-'}</p>
                                        <Row>
                                            <Col>
                                                <h6>{t('brand.campaign.briefing.usage_rights_duration')}</h6>
                                                <p>
                                                    {currentInformations?.usageRights?.duration
                                                        ? t(
                                                              `brand.campaign.briefing.step8.${currentInformations?.usageRights?.duration}`
                                                          )
                                                        : '-'}
                                                </p>
                                            </Col>
                                            <Col>
                                                <h6>{t('brand.campaign.briefing.usage_rights_location')}</h6>
                                                <p>
                                                    {!!currentInformations?.usageRights?.countries?.length
                                                        ? currentInformations?.usageRights?.countries?.map(
                                                              (country: { name: string }) => (
                                                                  <React.Fragment key={country?.name}>
                                                                      {t(`general.countries.${country?.name}`)},{' '}
                                                                  </React.Fragment>
                                                              )
                                                          )
                                                        : '-'}
                                                </p>
                                            </Col>
                                        </Row>
                                    </>
                                ) : (
                                    '-'
                                )}
                            </CardLayout>

                            <CardLayout classCard="mb-3">
                                <div className="d-flex justify-content-between">
                                    <h5>{t('brand.campaign.briefing.overview_card.legal')}</h5>
                                    {!isApproved && showEditIcon && (
                                        <TooltipOverlay content={t('brand.campaign.briefing.edit_section')}>
                                            <FaCog id="edit-icon" onClick={() => openStep(8)} />
                                        </TooltipOverlay>
                                    )}
                                </div>
                                <Row>
                                    <PayoutSectionOverview
                                        isLongTerm={isLongTermCampaign}
                                        paymentTerms={currentInformations?.paymentTerms}
                                        invoicePeriod={currentInformations?.invoicePeriod}
                                        paymentTermsCustom={currentInformations?.paymentTermsCustom}
                                    />
                                    <Col md={12}>
                                        <h6 className="text-muted">{t('brand.campaign.briefing.exclusivity')}</h6>
                                        <p>{currentInformations?.exclusivityDescription ?? '-'}</p>
                                    </Col>
                                    <Col md={6}>
                                        <h6 className="text-muted">
                                            {t('brand.campaign.briefing.exclusivity_duration')}
                                        </h6>
                                        <p>
                                            {currentInformations?.exclusivityDuration
                                                ? t(
                                                      `brand.campaign.briefing.step8.${currentInformations?.exclusivityDuration}`
                                                  )
                                                : '-'}
                                        </p>
                                    </Col>
                                    <Col md={6}>
                                        <h6 className="text-muted">{t('brand.campaign.briefing.competitors')}</h6>
                                        <p>
                                            {currentInformations?.exclusivityCompetitors?.length
                                                ? currentInformations?.exclusivityCompetitors?.map(
                                                      (competitor, index) => (
                                                          <span key={competitor?.id}>
                                                              {competitor?.description}
                                                              {currentInformations?.exclusivityCompetitors?.length - 1 >
                                                              index
                                                                  ? ', '
                                                                  : ''}
                                                          </span>
                                                      )
                                                  )
                                                : '-'}
                                        </p>
                                    </Col>
                                    <Col md={12}>
                                        <h6 className="text-muted"> {t('brand.campaign.briefing.bylaws')}</h6>
                                        {/*<p>{currentInformations?.byLawsExplanation ?? '-'}</p>*/}
                                        <div
                                            className="remove-html-styling"
                                            dangerouslySetInnerHTML={{
                                                __html: DOMPurify.sanitize(
                                                    currentInformations?.byLawsExplanation ?? '-',
                                                    {
                                                        ADD_ATTR: ['target'],
                                                    }
                                                ),
                                            }}
                                        ></div>
                                        <h6 className="text-muted"> {t('brand.campaign.briefing.bylaws_uploads')}</h6>

                                        <Fancybox>
                                            <Row>
                                                {!!currentInformations?.bylaws?.length ? (
                                                    currentInformations?.bylaws?.map(bylaws => {
                                                        return (
                                                            <div
                                                                className="col-md-4 my-2 position-relative"
                                                                key={bylaws?.id}
                                                            >
                                                                <FilePlaceholder
                                                                    file={bylaws}
                                                                    styleContent={{ height: 140 }}
                                                                    nameFancybox="bylaws-overview"
                                                                    handleDownload={() =>
                                                                        downloadFile(bylaws?.path, bylaws?.title)
                                                                    }
                                                                />
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <Col>-</Col>
                                                )}
                                            </Row>
                                        </Fancybox>
                                    </Col>
                                </Row>
                            </CardLayout>
                            <SelectGroupSection showEditIcon={showEditIcon} />
                            {isCharity && !!currentInformations?.charityValueDesc && isGeneralClient && (
                                <CardLayout classCard="mb-3">
                                    <ItemValueSection
                                        description={currentInformations?.charityValueDesc}
                                        displayTooltip={true}
                                    />
                                </CardLayout>
                            )}
                            {!isGeneralClient && (
                                <CardLayout classCard="mb-3">
                                    {!hasAccessRights(AccessRightEnum.CAN_EXPORT_MEASURE) || isCharity ? null : (
                                        <>
                                            {/*<Row className="mt-4 mb-2">*/}
                                            {/*    <Col md={3} className="pl-0 mb-3 mb-md-0">*/}
                                            {/*        <h6>{t('brand.campaign.briefing.suggested_budget_range')}</h6>*/}
                                            {/*    </Col>*/}
                                            {/*    <Col md={3}>*/}
                                            {/*        <h6>{t('brand.campaign.briefing.planned_budget')}</h6>*/}
                                            {/*    </Col>*/}
                                            {/*    {currentInformations?.brandedContent?.id && <Col md={3}>*/}
                                            {/*        <h6>{t('brand.campaign.book.offer.modal.branded_content')}</h6>*/}
                                            {/*    </Col>}*/}
                                            {/*    <Col md={currentInformations?.brandedContent?.id ? 3 : 6}*/}
                                            {/*         className="d-flex align-items-md-end my-3 my-md-0 flex-column justify-content-center">*/}
                                            {/*        <h6>{t('brand.campaign.briefing.total_budget')}</h6>*/}
                                            {/*    </Col>*/}
                                            {/*</Row>*/}
                                            <Row className="align-items-start">
                                                <Col md={3} className="mb-3 mb-md-0">
                                                    <h6 className="mb-md-3">
                                                        {t('brand.campaign.briefing.suggested_budget_range')}
                                                    </h6>
                                                    <h5 className="text-primary mb-0 euro-symbol">
                                                        {priceSplitter(
                                                            Math.round(
                                                                currentInformations?.creatorType?.budgetRanges?.[0]
                                                                    ?.recommendedMinBudget ?? 0
                                                            )
                                                        )}
                                                        -{' '}
                                                        {priceSplitter(
                                                            Math.round(
                                                                currentInformations?.creatorType?.budgetRanges?.[0]
                                                                    ?.recommendedMaxBudget ?? 0
                                                            )
                                                        )}
                                                    </h5>
                                                </Col>
                                                <Col md={3}>
                                                    <h6 className="mb-md-3">
                                                        {t('brand.campaign.briefing.planned_budget')}
                                                    </h6>
                                                    <TextField
                                                        label={`${t('brand.campaign.briefing.content_creator_budget')}`}
                                                        variant="outlined"
                                                        size="small"
                                                        type="number"
                                                        onChange={changeHandler}
                                                        InputProps={{ inputProps: { min: 0 } }}
                                                        onKeyPress={event =>
                                                            (event?.key === '-' ||
                                                                event?.key === '+' ||
                                                                event?.key === '.') &&
                                                            event.preventDefault()
                                                        }
                                                        value={currentInformations?.grossBudget ?? ''}
                                                        error={displayBudgetError && !isCharity && !isChildCampaign}
                                                        name="grossBudget"
                                                        className="w-100"
                                                    />
                                                    {!isCharity && displayBudgetError && !isChildCampaign && (
                                                        <span className="text-danger">
                                                            {t('brand.campaign.briefing.moreThan1000')}
                                                        </span>
                                                    )}
                                                </Col>
                                                {currentInformations?.brandedContent?.id && (
                                                    <Col md={3} className="my-3 my-md-0">
                                                        <h6 className="mb-md-3">
                                                            {t('brand.campaign.book.offer.modal.branded_content')}
                                                        </h6>
                                                        <TextField
                                                            label={`${t('brand.campaign.briefing.performance_budget')}`}
                                                            variant="outlined"
                                                            value={currentInformations?.brandedContent?.budget ?? 0}
                                                            disabled
                                                            size="small"
                                                            type="text"
                                                            name="budget"
                                                            className="w-100"
                                                        />
                                                    </Col>
                                                )}
                                                <Col
                                                    md={currentInformations?.brandedContent?.id ? 3 : 6}
                                                    className="my-3 my-md-0 text-md-end"
                                                >
                                                    <h6 className="mb-md-3">
                                                        {t('brand.campaign.briefing.total_budget')}
                                                    </h6>
                                                    <div className="d-flex align-items-center justify-content-md-end">
                                                        <h3 className="text-primary mb-0 euro-symbol">
                                                            {priceSplitter(
                                                                (currentInformations?.brandedContent
                                                                    ? +currentInformations?.brandedContent?.budget
                                                                    : 0) +
                                                                    (currentInformations?.grossBudget
                                                                        ? +currentInformations?.grossBudget
                                                                        : 0)
                                                            )}
                                                        </h3>
                                                        <TooltipOverlay
                                                            placement="right"
                                                            className="ms-2"
                                                            content={t('general.totalBudgetDescription')}
                                                        >
                                                            <HiOutlineInformationCircle size={20} />
                                                        </TooltipOverlay>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </>
                                    )}
                                    <Row className={' flex-md-row '}>
                                        <Col className="mb-3 mb-md-0 mt-md-0" md={12}>
                                            {isCharity && (
                                                <div className="mb-3">
                                                    <div className="d-flex align-items-center mb-3">
                                                        <h6 className="mb-0">
                                                            {t(`brand.campaign.briefing.item_value`)}
                                                        </h6>
                                                        <TooltipOverlay
                                                            className="ms-1 mb-0"
                                                            content={t(`brand.campaign.briefing.item_value_tooltip`)}
                                                        >
                                                            <FiInfo size={14} />
                                                        </TooltipOverlay>
                                                    </div>
                                                    {isGeneralClient ? (
                                                        <p className="text-muted">
                                                            {currentInformations?.charityValueDesc}
                                                        </p>
                                                    ) : (
                                                        <Form.Control
                                                            id="basic-textarea"
                                                            onChange={changeHandler}
                                                            defaultValue={currentInformations?.charityValueDesc}
                                                            as="textarea"
                                                            name="charityValueDesc"
                                                            rows={3}
                                                            disabled={isGeneralClient}
                                                            placeholder={t(
                                                                'brand.campaign.briefing.item_value_placeholder'
                                                            )}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </Col>
                                        <Col className="mb-3 mb-md-0 mt-2 mt-md-0" md={6}>
                                            <h6>{t(`brand.campaign.briefing.publishing`)}</h6>
                                            <div className="d-flex align-items-center mt-lg-4">
                                                <p className="mb-0 mr-3">
                                                    {t(
                                                        `brand.campaign.briefing.${
                                                            currentPublishStatus === 'published' ? 'public' : 'private'
                                                        }`
                                                    )}
                                                </p>
                                                <TooltipPackage
                                                    show={!company?.package?.allowPrivateCampaigns}
                                                    trigger="click"
                                                    description="noPrivateCampaigns"
                                                >
                                                    <div>
                                                        <ToggleSwitch
                                                            small={true}
                                                            disabled={
                                                                !company?.package?.allowPrivateCampaigns ||
                                                                currentInformations?.isLongTerm
                                                            }
                                                            id="content-approval"
                                                            onChange={() => {
                                                                setCurrentPublishStatus(prev =>
                                                                    prev === 'published' ? 'private' : 'published'
                                                                );
                                                            }}
                                                            checked={currentPublishStatus === 'published'}
                                                        />
                                                    </div>
                                                </TooltipPackage>
                                                <TooltipOverlay
                                                    placement="right"
                                                    className="ms-2"
                                                    content={
                                                        <div className="d-flex flex-column">
                                                            <div>{t('general.publicDescription')}</div>
                                                            <div className="mt-2">
                                                                {t('general.privateDescription')}
                                                            </div>
                                                        </div>
                                                    }
                                                >
                                                    <HiOutlineInformationCircle size={20} />
                                                </TooltipOverlay>
                                                <p className="mb-0 mx-3">
                                                    {t('brand.account.price_list_modal.barter_deals')}
                                                </p>
                                                <div className="d-flex align-items-center">
                                                    <TooltipPackage
                                                        show={!company?.package?.allowBarterDeals}
                                                        trigger="click"
                                                        description={'noBarterDeal'}
                                                    >
                                                        <div>
                                                            <ToggleSwitch
                                                                small={true}
                                                                disabled={!company?.package?.allowBarterDeals}
                                                                onChange={(isChecked: boolean) => {
                                                                    if (company?.package?.allowBarterDeals) {
                                                                        setCurrentInformations((prev: any) => ({
                                                                            ...prev,
                                                                            isCharity: isChecked,
                                                                        }));
                                                                    }
                                                                }}
                                                                id="barter_deail_approval"
                                                                checked={isCharity}
                                                            />
                                                        </div>
                                                    </TooltipPackage>
                                                    <TooltipOverlay
                                                        placement="right"
                                                        className="ms-2"
                                                        content={
                                                            <div className="d-flex flex-column">
                                                                {t('brand.account.price_list_modal.barter_deals_desc')}
                                                            </div>
                                                        }
                                                    >
                                                        <HiOutlineInformationCircle size={20} />
                                                    </TooltipOverlay>
                                                </div>
                                            </div>
                                        </Col>
                                        {!isCharity ||
                                            (!hasAccessRights(AccessRightEnum.CAN_EXPORT_MEASURE) && (
                                                <Col md={6} className="text-md-end">
                                                    <div>
                                                        <a
                                                            className="text-underline"
                                                            onClick={() =>
                                                                openCalendlyPopup(
                                                                    String(process.env.VITE_PACKAGE_UPGRADE_CALENDLY)
                                                                )
                                                            }
                                                        >
                                                            <h5>
                                                                {t(
                                                                    `brand.package.${
                                                                        company?.package?.name === PackagesEnum.BASIC
                                                                            ? 'saveWithPro'
                                                                            : 'savedWithPro'
                                                                    }`
                                                                )}
                                                            </h5>
                                                        </a>
                                                    </div>
                                                    <h3 className="text-muted text-line-through mb-0 euro-symbol opacity-25">
                                                        {priceSplitter(
                                                            Math.round(
                                                                calculateSavedAmount(
                                                                    currentInformations?.grossBudget ?? 1,
                                                                    Number(company?.fee)
                                                                )
                                                            )
                                                        )}
                                                    </h3>
                                                </Col>
                                            ))}
                                    </Row>
                                    <div className="mt-lg-4">
                                        <StepperRouting
                                            status={currentInformations?.status}
                                            approvalStatus={currentInformations?.approvalStatus}
                                            submitStepHandler={() => {
                                                if (!completionInfo?.billingInfo) {
                                                    setIsBillingInfoWarningOpened(true);
                                                    return;
                                                }
                                                if (currentPublishStatus === 'published') {
                                                    if (
                                                        (currentInformations?.grossBudget ?? 0) >= 1000 ||
                                                        isChildCampaign ||
                                                        isCharity ||
                                                        !hasAccessRights(AccessRightEnum.CAN_EXPORT_MEASURE)
                                                    ) {
                                                        setShowConfirmModal(true);
                                                        setDisplayBudgetError(false);
                                                    } else {
                                                        setDisplayBudgetError(true);
                                                    }
                                                } else {
                                                    publishCampaignHandler();
                                                }
                                            }}
                                        />
                                    </div>
                                </CardLayout>
                            )}
                        </>
                    )}
                    <div className="d-block d-md-none mt-4">
                        <FindCreatorsInfo />
                    </div>
                </Col>
                <Col xl={4} className="position-relative pr-md-4 mb-3">
                    <CampaignOverviewCard
                        triggerUpdate={showBriefingModal}
                        openEditCampaignModal={isGeneralClient ? undefined : openModal}
                    />
                </Col>
            </Row>
        </>
    );
};
